import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone, Clock } from "lucide-react";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Installation",
  "Modernization",
  "Maintenance & Repair",
  "Accessibility Lifts",
  "Emergency Service",
  "Consultation",
];

const BUILDING_OPTIONS = ["Residential", "Commercial", "Institutional", "Industrial", "Other"];

const coverage = [
  "Downtown Toronto",
  "North York",
  "Scarborough",
  "Etobicoke",
  "Mississauga",
  "Brampton",
  "Vaughan",
  "Markham",
  "Richmond Hill",
  "Oakville",
  "Burlington",
  "Hamilton",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  service: "Installation",
  building_type: "Commercial",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name || !form.email || !form.phone || !form.message) {
      setError("Please fill in name, email, phone and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setSubmitted(true);
      setForm(initial);
      toast.success("Request received. A Vertex representative will call within one business hour.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : detail || "Something went wrong. Please try again or call (437) 494-0799.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="border-b border-[#E5E5E5] py-24 md:py-32 bg-[#F9F9F9]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: intro + info */}
          <div className="lg:col-span-5">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#FF5E00] mb-4">
              Request a Quote · 24/7 Service
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Let&rsquo;s talk
              <br /> vertical.
            </h2>
            <p className="mt-6 text-[#525252] text-base leading-relaxed max-w-md">
              Tell us about your building and what you need. A senior estimator
              will respond within one business hour — or call our 24/7 line if
              it can&rsquo;t wait.
            </p>

            <div className="mt-10 space-y-6">
              <InfoRow icon={<Phone size={16} />} label="24/7 Emergency" value="(437) 494-0799" href="tel:+14374940799" />
              <InfoRow icon={<Mail size={16} />} label="Estimates" value="quotes@vertexelevators.ca" href="mailto:quotes@vertexelevators.ca" />
              <InfoRow icon={<MapPin size={16} />} label="Headquarters" value="1420 Ellesmere Rd, Scarborough, ON M1P 2X6" />
              <InfoRow icon={<Clock size={16} />} label="Office Hours" value="Mon–Fri · 7:00–18:00 EST" />
            </div>

            <div className="mt-12">
              <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#525252] mb-4">
                Service Coverage
              </div>
              <div className="flex flex-wrap gap-2">
                {coverage.map((c) => (
                  <span
                    key={c}
                    className="text-[10px] font-bold uppercase tracking-[0.24em] border border-[#0A0A0A] px-3 py-1.5 bg-white"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right: form */}
          <motion.form
            data-testid={CONTACT.form}
            onSubmit={onSubmit}
            noValidate
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 bg-white border border-[#0A0A0A] p-8 md:p-10 vx-hard-shadow"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <Field label="Full Name">
                <input
                  data-testid={CONTACT.inputName}
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Jane Doe"
                  className="w-full bg-transparent border-b border-[#0A0A0A] rounded-none px-0 py-3 focus:outline-none focus:border-[#FF5E00] placeholder:text-gray-400 text-[#0A0A0A]"
                  required
                />
              </Field>
              <Field label="Email">
                <input
                  data-testid={CONTACT.inputEmail}
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="jane@company.ca"
                  className="w-full bg-transparent border-b border-[#0A0A0A] rounded-none px-0 py-3 focus:outline-none focus:border-[#FF5E00] placeholder:text-gray-400 text-[#0A0A0A]"
                  required
                />
              </Field>
              <Field label="Phone">
                <input
                  data-testid={CONTACT.inputPhone}
                  value={form.phone}
                  onChange={onChange("phone")}
                  placeholder="(416) 555-0100"
                  className="w-full bg-transparent border-b border-[#0A0A0A] rounded-none px-0 py-3 focus:outline-none focus:border-[#FF5E00] placeholder:text-gray-400 text-[#0A0A0A]"
                  required
                />
              </Field>
              <Field label="Service Needed">
                <select
                  data-testid={CONTACT.inputService}
                  value={form.service}
                  onChange={onChange("service")}
                  className="w-full bg-transparent border-b border-[#0A0A0A] rounded-none px-0 py-3 focus:outline-none focus:border-[#FF5E00] text-[#0A0A0A]"
                >
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Building Type">
                <select
                  data-testid={CONTACT.inputBuilding}
                  value={form.building_type}
                  onChange={onChange("building_type")}
                  className="w-full bg-transparent border-b border-[#0A0A0A] rounded-none px-0 py-3 focus:outline-none focus:border-[#FF5E00] text-[#0A0A0A]"
                >
                  {BUILDING_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Message" full>
                <textarea
                  data-testid={CONTACT.inputMessage}
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Tell us about your building, number of units and any issues you're facing…"
                  rows={5}
                  className="w-full bg-transparent border-b border-[#0A0A0A] rounded-none px-0 py-3 focus:outline-none focus:border-[#FF5E00] placeholder:text-gray-400 text-[#0A0A0A] resize-none"
                  required
                />
              </Field>
            </div>

            {error && (
              <div
                data-testid={CONTACT.error}
                role="alert"
                className="mt-6 border border-[#0A0A0A] bg-[#FF5E00] text-white px-4 py-3 text-sm font-medium"
              >
                {error}
              </div>
            )}
            {submitted && !error && (
              <div
                data-testid={CONTACT.success}
                role="status"
                className="mt-6 border border-[#0A0A0A] bg-[#0A0A0A] text-white px-4 py-3 text-sm font-medium"
              >
                Request received. Our team will be in touch shortly.
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 pt-8 border-t border-[#E5E5E5]">
              <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#525252]">
                Response · Within 1 business hour
              </div>
              <button
                data-testid={CONTACT.submit}
                type="submit"
                disabled={submitting}
                className="group inline-flex items-center gap-3 bg-[#0A0A0A] hover:bg-[#FF5E00] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.24em] transition-colors"
              >
                {submitting ? "Sending…" : "Send Request"}
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#525252] mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

function InfoRow({ icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="mt-0.5 h-8 w-8 shrink-0 border border-[#0A0A0A] flex items-center justify-center bg-white">
        {icon}
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#525252]">
          {label}
        </div>
        <div className="mt-1 font-display font-bold text-lg text-[#0A0A0A]">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block group hover:text-[#FF5E00]">
      {content}
    </a>
  ) : (
    content
  );
}
